import React from 'react';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ExportOptionsTab = ({ settings, onSettingChange }) => {
  const exportFormatOptions = [
    { value: 'pdf', label: 'PDF Document' },
    { value: 'excel', label: 'Excel Spreadsheet' },
    { value: 'csv', label: 'CSV File' }
  ];

  const pageOrientationOptions = [
    { value: 'portrait', label: 'Portrait' },
    { value: 'landscape', label: 'Landscape' }
  ];

  const pageSizeOptions = [
    { value: 'letter', label: 'Letter (8.5" x 11")' },
    { value: 'legal', label: 'Legal (8.5" x 14")' },
    { value: 'a4', label: 'A4 (210mm x 297mm)' }
  ];

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onSettingChange('companyLogo', e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    onSettingChange('companyLogo', '');
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Default Export Settings</h3>
        <p className="text-sm text-text-secondary mb-6">Configure default options for exporting amortization schedules and reports.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Default Export Format"
            options={exportFormatOptions}
            value={settings.defaultExportFormat}
            onChange={(value) => onSettingChange('defaultExportFormat', value)}
            description="Primary format for exported reports"
          />
          
          <Select
            label="Page Orientation"
            options={pageOrientationOptions}
            value={settings.pageOrientation}
            onChange={(value) => onSettingChange('pageOrientation', value)}
            description="Layout orientation for printed reports"
          />
          
          <Select
            label="Page Size"
            options={pageSizeOptions}
            value={settings.pageSize}
            onChange={(value) => onSettingChange('pageSize', value)}
            description="Paper size for printed documents"
            className="md:col-span-2"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Report Content</h3>
        <p className="text-sm text-text-secondary mb-6">Choose which sections to include in exported reports by default.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Checkbox
            label="Include Loan Summary"
            checked={settings.includeLoanSummary}
            onChange={(e) => onSettingChange('includeLoanSummary', e.target.checked)}
            description="Basic loan details and totals"
          />
          
          <Checkbox
            label="Include Payment Schedule"
            checked={settings.includePaymentSchedule}
            onChange={(e) => onSettingChange('includePaymentSchedule', e.target.checked)}
            description="Full amortization table"
          />
          
          <Checkbox
            label="Include Charts"
            checked={settings.includeCharts}
            onChange={(e) => onSettingChange('includeCharts', e.target.checked)}
            description="Visual payment breakdown graphs"
          />
          
          <Checkbox
            label="Include Yearly Totals"
            checked={settings.includeYearlyTotals}
            onChange={(e) => onSettingChange('includeYearlyTotals', e.target.checked)}
            description="Annual payment summaries"
          />
          
          <Checkbox
            label="Include Calculation Notes"
            checked={settings.includeCalculationNotes}
            onChange={(e) => onSettingChange('includeCalculationNotes', e.target.checked)}
            description="Methodology and assumptions"
          />
          
          <Checkbox
            label="Include Disclaimer"
            checked={settings.includeDisclaimer}
            onChange={(e) => onSettingChange('includeDisclaimer', e.target.checked)}
            description="Legal disclaimer text"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Report Customization</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Report Header Text"
              type="text"
              placeholder="Loan Amortization Schedule"
              value={settings.reportHeaderText}
              onChange={(e) => onSettingChange('reportHeaderText', e.target.value)}
              description="Main title for exported reports"
            />
            
            <Input
              label="Report Footer Text"
              type="text"
              placeholder="Generated by AmortizationPro"
              value={settings.reportFooterText}
              onChange={(e) => onSettingChange('reportFooterText', e.target.value)}
              description="Footer text for all pages"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Company Logo</label>
            <p className="text-xs text-text-secondary mb-4">Upload your company logo to appear on exported reports (recommended size: 200x80px)</p>
            
            {settings.companyLogo ? (
              <div className="flex items-center space-x-4 p-4 border border-border rounded-lg bg-surface">
                <div className="flex-shrink-0">
                  <img 
                    src={settings.companyLogo} 
                    alt="Company Logo" 
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Logo uploaded successfully</p>
                  <p className="text-xs text-text-secondary">This logo will appear on all exported reports</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Trash2"
                  onClick={handleRemoveLogo}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                <Icon name="Upload" size={32} className="mx-auto text-text-secondary mb-2" />
                <p className="text-sm text-text-secondary mb-4">No logo uploaded</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Upload"
                  iconPosition="left"
                  onClick={() => document.getElementById('logo-upload').click()}
                >
                  Upload Logo
                </Button>
              </div>
            )}
          </div>
          
          <Input
            label="Disclaimer Text"
            type="text"
            placeholder="This calculation is for informational purposes only..."
            value={settings.disclaimerText}
            onChange={(e) => onSettingChange('disclaimerText', e.target.value)}
            description="Legal disclaimer to include in reports"
          />
        </div>
      </div>
    </div>
  );
};

export default ExportOptionsTab;